// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { TenantsSearchPageCmp, TenantsSearchPageProps } from "./types";
import { TENANT_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Button } from "ui/components/Button/Button";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";

// Lokalne komponente
import TenantsSearchResults from "./components/TenantsSearchResults/TenantsSearchResults";
import TenantsSearchFilters from "./components/TenantsSearchFilters/TenantsSearchFilters";
import TenantEditCard from "./components/TenantEditCard/TenantEditCard";

import {
  TenantEventType,
  TenantRow,
  TenantSearchFormValues,
  TenantStorage,
} from "services/api/tenants/types";
import { getAllTenantsStorageAPI, searchTenantsAPI } from "services/api/tenants/api";
import { initialTenantSearchForm } from "services/api/tenants/consts";

const TenantsSearchPage: TenantsSearchPageCmp = (
  props: TenantsSearchPageProps
) => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<TenantRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showWizard, setShowWizard] = useState<boolean>(false);
  const [storageByTenant, setStorageByTenant] = useState<Record<string, TenantStorage>>({});
  const [filterValues, setFilterValues] = useState<TenantSearchFormValues>(
    initialTenantSearchForm
  );

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
    fetchStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStorage = async () => {
    try {
      const list = await getAllTenantsStorageAPI();
      const map: Record<string, TenantStorage> = {};
      list.forEach((s) => {
        map[s.tenantId] = s;
      });
      setStorageByTenant(map);
    } catch {
      setStorageByTenant({});
    }
  };

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await searchTenantsAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const navigateToDetail = useCallback(
    (id: string) => {
      navigate(
        transformRoute(Routes.TenantDetailPageRouter, {
          tenantId: id,
        })
      );
    },
    [navigate]
  );

  const handleRowClick = useCallback(
    (id: string) => {
      navigateToDetail(id);
    },
    [navigateToDetail]
  );

  const handleEventClick = useCallback(
    (id: string, name: string) => {
      if (name === TenantEventType.DETAIL) {
        navigateToDetail(id);
      }
    },
    [navigateToDetail]
  );

  const handleAddNew = useCallback(() => {
    setShowWizard(true);
  }, []);

  const handleSearchChange = useCallback(
    (searchFormValues: TenantSearchFormValues) => {
      setFilterValues(searchFormValues);

      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination, filterValues.search);
  };

  const handleWizardSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination, filterValues.search);
    setShowWizard(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, searchResults.pagination]);

  const handleWizardClose = useCallback(() => {
    setShowWizard(false);
    fetchSearchResult(searchResults.pagination, filterValues.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, searchResults.pagination]);

  return (
    <>
      <div className="">
   <div className="bg-white rounded-t-md border-b border-slate-200">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div>
                <PageTitle
                  title={TENANT_SEARCH_PAGE_TITLE.text}
                  icon={TENANT_SEARCH_PAGE_TITLE.icon}
                  iconColor={TENANT_SEARCH_PAGE_TITLE.iconColor}
                />
                <Breadcrumb />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  children="Novi tenant"
                  startIcon="/media/icons/plus.svg"
                  onClick={handleAddNew}
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <TenantsSearchFilters
            formValues={filterValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Dodajte tenanta klikom na 'Novi tenant'" />
          ) : (
            <TenantsSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              storageByTenant={storageByTenant}
              onRowClick={handleRowClick}
              onEventClick={handleEventClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>

      <TenantEditCard
        show={showWizard}
        onSuccess={handleWizardSuccess}
        onClose={handleWizardClose}
      />
    </>
  );
};

TenantsSearchPage.route = Routes.TenantSearchPageRouter;

export default TenantsSearchPage;
